import { useDeferredValue, useMemo, useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Faq, Prose, Section, linkClass } from '@/components/converter/PageParts';
import {
  Checkbox,
  HowTo,
  MoreTools,
  NextSteps,
  Segmented,
  ToolCard,
  ToolHead,
  ToolHero,
  useGzipSize,
  useLocalOnlyBreadcrumbs,
  useMounted,
  type FaqItem,
} from '@/components/svg-tools/ToolParts';
import { SvgInput } from '@/components/svg-tools/SvgInput';
import { CodeOutput } from '@/components/svg-tools/CodeOutput';
import { SAMPLE } from '@/components/svg-tools/sample';
import { formatBytes, inspectSvg, normalizeSvg, parseSvg } from '@/utils/svg-tools/svg-source';
import {
  base64DataUri,
  cssBackgroundSnippet,
  cssMaskSnippet,
  imgSnippet,
  prepareSvg,
  urlEncodedDataUri,
} from '@/utils/svg-tools/data-uri';

const PATH = '/svg-to-data-uri';
const TITLE = 'SVG to Data URI: CSS Background Image Converter | SVG.new';
const DESCRIPTION =
  'Convert SVG to a data URI for CSS background-image or <img> src. URL-encoded output is smaller than base64; both are ready to copy. Runs in your browser.';

const code = 'px-1.5 py-0.5 bg-cream-100 rounded text-[13px] font-mono [font-variant-ligatures:none] text-ink-soft';

const faq: FaqItem[] = [
  {
    q: 'Is a URL-encoded SVG data URI smaller than base64?',
    a: 'Usually. Base64 turns every 3 bytes into 4 characters, so it is about a third larger than the SVG. URL-encoding keeps the SVG as text and escapes only the characters a URL cannot hold, such as <, >, # and %, and it also compresses better with gzip. The tool shows both sizes for your SVG.',
  },
  {
    q: 'Why are the double quotes changed to single quotes?',
    a: 'So the data URI can sit inside url("…") in CSS and inside src="…" in HTML without more escaping. Only the quotes around attribute values change. Any other double quote, such as one in your text, is percent-encoded as %22, so the picture stays the same.',
  },
  {
    q: 'Can I change the color of an SVG background image with CSS?',
    a: 'Not with fill or color: a background image is drawn on its own and does not see your page’s CSS. Use the CSS mask snippet instead. The SVG becomes a mask and background-color paints it, so currentColor makes it follow the text color. Or change the fill in the SVG before you convert it.',
  },
  {
    q: 'Do I need ;charset=utf-8 or ;utf8 in the data URI?',
    a: 'No. Characters outside ASCII are percent-encoded as UTF-8 bytes, which is what an SVG file is read as by default. ;utf8 is not a valid parameter anyway; browsers just ignore it.',
  },
  {
    q: 'Is my SVG uploaded?',
    a: 'No. The data URIs are made in your browser, and the SVG never leaves your computer.',
  },
];

type Encoding = 'url' | 'base64';

export default function SvgToDataUriPage() {
  useLocalOnlyBreadcrumbs();
  const mounted = useMounted();
  const [text, setText] = useState(SAMPLE.text);
  const [collapse, setCollapse] = useState(true);
  const [encoding, setEncoding] = useState<Encoding>('url');
  const deferred = useDeferredValue(text);

  const out = useMemo(() => {
    if (!mounted) return null;
    const normalized = normalizeSvg(deferred);
    const parsed = parseSvg(normalized.text);
    if (!parsed.ok) return parsed;
    const info = inspectSvg(parsed.root);
    const svg = prepareSvg(normalized.text, collapse);
    return {
      ok: true as const,
      fixes: normalized.fixes,
      info,
      url: urlEncodedDataUri(svg),
      base64: base64DataUri(svg),
    };
  }, [mounted, deferred, collapse]);

  const ok = out?.ok ? out : null;
  const urlGzip = useGzipSize(ok ? ok.url : null);
  const base64Gzip = useGzipSize(ok ? ok.base64 : null);
  const uri = ok ? (encoding === 'url' ? ok.url : ok.base64) : '';

  return (
    <>
      <ToolHead
        path={PATH}
        title={TITLE}
        description={DESCRIPTION}
        socialTitle="SVG to data URI converter"
        appName="SVG.new SVG to data URI converter"
        crumb="SVG to data URI"
        featureList={[
          'URL-encoded SVG data URI, smaller than base64',
          'Base64 SVG data URI',
          'CSS background-image snippet',
          'CSS mask snippet that takes the text color',
          'HTML <img src> snippet with width and height',
          'Plain and gzipped sizes of both encodings',
        ]}
        faq={faq}
      />
      <div className="min-h-screen bg-cream-50 flex flex-col font-sans">
        <SiteHeader />

        <main className="flex-1">
          <ToolHero
            crumb="SVG to data URI"
            before="SVG to "
            highlight="data URI"
            after=" converter"
            intro="Paste an SVG or open a file and get a data URI to use as a CSS background-image or an <img> src: URL-encoded, which is smaller, or base64."
          >
            <ToolCard>
              <div className="grid min-w-0 lg:grid-cols-2">
                <div className="min-w-0 border-b border-cream-200 lg:border-b-0 lg:border-r">
                  <SvgInput
                    value={text}
                    sample={SAMPLE}
                    heightClass="h-[220px] sm:h-[300px]"
                    onChange={(next) => setText(next)}
                  />
                </div>
                <div className="min-w-0 p-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <div
                      role="img"
                      aria-label="The data URI used as a CSS background image"
                      className="checker-bg h-28 w-28 shrink-0 rounded-lg border border-cream-200 bg-center bg-no-repeat"
                      style={ok ? { backgroundImage: `url("${ok.url}")`, backgroundSize: '80% 80%' } : undefined}
                    />
                    <p className="text-[13px] leading-relaxed text-ink-muted">
                      {out && !out.ok
                        ? out.error
                        : 'This box uses the URL-encoded data URI as its CSS background-image, so what you see is what the snippet draws.'}
                    </p>
                  </div>
                  {ok && (
                    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]">
                      <dt className="text-ink-muted">URL-encoded</dt>
                      <dd className="font-mono text-[12.5px] text-ink">
                        {formatBytes(ok.url.length)}
                        {urlGzip !== null && <span className="text-ink-muted"> · {formatBytes(urlGzip)} gzipped</span>}
                      </dd>
                      <dt className="text-ink-muted">Base64</dt>
                      <dd className="font-mono text-[12.5px] text-ink">
                        {formatBytes(ok.base64.length)}
                        {base64Gzip !== null && <span className="text-ink-muted"> · {formatBytes(base64Gzip)} gzipped</span>}
                      </dd>
                      <dt className="text-ink-muted">Difference</dt>
                      <dd className="text-ink">
                        URL-encoded is {Math.abs(Math.round((1 - ok.url.length / ok.base64.length) * 100))}%{' '}
                        {ok.url.length <= ok.base64.length ? 'smaller' : 'larger'}
                      </dd>
                    </dl>
                  )}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    <Checkbox checked={collapse} onChange={setCollapse}>
                      Collapse whitespace
                    </Checkbox>
                    <div className="flex items-center gap-2 text-[13px] text-ink-soft">
                      Snippets use
                      <Segmented
                        label="Encoding used in the snippets"
                        value={encoding}
                        onChange={setEncoding}
                        options={[
                          { value: 'url', label: 'URL-encoded' },
                          { value: 'base64', label: 'Base64' },
                        ]}
                      />
                    </div>
                  </div>
                  {ok && ok.fixes.length > 0 && <p className="text-[12.5px] text-ink-muted">{ok.fixes.join(' ')}</p>}
                </div>
              </div>
              {ok && (
                <div className="grid min-w-0 gap-3 border-t border-cream-200 p-4 lg:grid-cols-2">
                  <CodeOutput label="Data URI, URL-encoded" code={ok.url} wrap heightClass="max-h-[160px]" />
                  <CodeOutput label="Data URI, base64" code={ok.base64} wrap heightClass="max-h-[160px]" />
                  <CodeOutput label="CSS background-image" code={cssBackgroundSnippet(uri)} wrap heightClass="max-h-[200px]" />
                  <CodeOutput
                    label="CSS mask, colored by the text color"
                    code={cssMaskSnippet(uri, ok.info.width, ok.info.height)}
                    wrap
                    heightClass="max-h-[200px]"
                  />
                  <div className="lg:col-span-2 min-w-0">
                    <CodeOutput
                      label="HTML <img>"
                      code={imgSnippet(uri, ok.info.width, ok.info.height)}
                      wrap
                      heightClass="max-h-[160px]"
                    />
                  </div>
                </div>
              )}
            </ToolCard>
          </ToolHero>

          <NextSteps
            agentText={
              <>
                Your agent can paste a data URI into a stylesheet already. Add the SVG.new MCP server and Claude Code or
                Codex can also make the SVG first, from a PNG, JPG or WebP in your repo, and recolor it to match.
              </>
            }
          />

          <Section heading="How to use an SVG as a data URI">
            <HowTo
              steps={[
                {
                  title: 'Paste the SVG or open the file',
                  text: 'Paste the markup or drop an .svg file on the code box. Small, simple SVGs such as icons, arrows and patterns make the best data URIs.',
                },
                {
                  title: 'Copy the data URI or a snippet',
                  text: 'The URL-encoded data URI is the smaller one. The CSS and <img> snippets use whichever encoding you pick, and the <img> tag gets the width and height of your SVG.',
                },
                {
                  title: 'Paste it into your CSS or HTML',
                  text: (
                    <>
                      Keep the double quotes around the URI in <code className={code}>url(&quot;…&quot;)</code> and{' '}
                      <code className={code}>src=&quot;…&quot;</code>; the URI itself uses single quotes inside.
                    </>
                  ),
                },
              ]}
            />
          </Section>

          <Section heading="URL-encoded or base64?" tone="cream">
            <Prose>
              <p>
                Base64 turns every 3 bytes of the SVG into 4 characters, so the data URI is about 33% larger than the
                file, and gzip compresses it less well than the plain text. URL-encoding leaves the SVG readable and replaces only
                the characters that would break the URL or the CSS string: <code className={code}>&lt;</code>,{' '}
                <code className={code}>&gt;</code>, <code className={code}>#</code>, <code className={code}>%</code>{' '}
                and a few more. Spaces, <code className={code}>=</code>, <code className={code}>:</code> and{' '}
                <code className={code}>/</code> stay as they are, because browsers accept them in a data URI.
              </p>
              <p>
                Use base64 when a tool or a framework only accepts base64. Otherwise the URL-encoded version is smaller
                and you can still read and edit it.
              </p>
              <p>
                Large or detailed SVGs are better as separate files, which the browser can cache. Shrink the SVG with
                the <Link href="/svg-viewer" className={linkClass}>SVG optimizer</Link> before you convert it; for
                React, the <Link href="/svg-to-react" className={linkClass}>SVG to JSX converter</Link> makes a
                component instead.
              </p>
            </Prose>
          </Section>

          <Section heading="Questions about SVG data URIs">
            <Faq items={faq} />
          </Section>

          <MoreTools current={PATH} />
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
